import {useGSAP} from '@gsap/react';
import {gsap} from 'gsap';
import {RefObject, useCallback, useRef} from 'react';

const ANIMATION_COMPLETE_DELAY = 3000;

interface TimelineRef {
  envelopeTimeline: gsap.core.Timeline;
  cardTimeline: gsap.core.Timeline;
}

interface UseCardAnimationProps {
  containerRef: RefObject<HTMLDivElement | null>;
  containerWidth: number;
  containerHeight: number;
  cardWidth: number;
  envelopeHeight: number;
  noAnimation: boolean;
  envelopeScaleFinal: number;
  hasPlayedRef: RefObject<boolean>;
  setAnimationComplete: (v: boolean) => void;
}

export const useCardAnimation = ({
  containerRef,
  containerWidth,
  containerHeight,
  cardWidth,
  envelopeHeight,
  noAnimation,
  envelopeScaleFinal,
  hasPlayedRef,
  setAnimationComplete,
}: UseCardAnimationProps) => {
  const tlRef = useRef<TimelineRef | null>(null);
  const shouldPlayRef = useRef<boolean>(false);

  useGSAP(
    () => {
      const envelopeTimeline = gsap.timeline({
        paused: true,
        defaults: {ease: 'sine.inOut'},
        onComplete: () => {
          hasPlayedRef.current = true;
          setTimeout(() => {
            setAnimationComplete(true);
          }, ANIMATION_COMPLETE_DELAY);
        },
      });

      // Envelope Animation
      envelopeTimeline
        // Envelope flips over (x-axis)
        .to('.env-group__stamp-side', {rotateY: 180, duration: 1.5}, 1)
        .to(
          ['.env-group__closed-flap', '.env-group__open-bottom-front'],
          {rotateY: 0, duration: 1.5},
          1
        )
        .set('.env-group__stamp-side', {display: 'none'}, 2)
        // Envelope flap opens
        .set(['.bottom-back', '.open-flap'], {display: 'block'}, 2.3)
        .to('.env-group__closed-flap', {rotateX: 180, duration: 1}, 2.3)
        .to('.env-group__open-flap', {rotateX: 0, duration: 1}, 2.3)
        .set('.env-group__closed-flap', {display: 'none'}, 3.5)
        // Envelope moves down and left
        .to('.env-final', {translateY: `+=${envelopeHeight}`, ease: 'linear', duration: 1.2}, 2.5)

        .to(
          '.env-final',
          {
            translateY: `50%`,
            scale: envelopeScaleFinal,
            translateX: -(cardWidth / 2),
            ease: 'sine.inOut',
            duration: 1.4,
          },
          3.7
        );

      const cardTimeline = gsap.timeline({
        paused: true,
        defaults: {ease: 'power1.inOut'},
      });

      // Card Animation
      cardTimeline
        // Reset card styles
        .set('.animation-replay-btn', {opacity: '0'}, 0)
        .set(
          ['.animation-card', '.animation-card__wrapper'],
          {clearProps: 'transform, height, display'},
          0
        )
        .set('.animation-card__wrapper', {zIndex: 0}, 0)
        .set('.animation-card', {display: 'none'}, 0)
        .set('.animation-card', {display: 'block'}, 2.3)
        .to('.animation-card', {height: '100%', duration: 0.4, ease: 'linear'}, 3.1)
        // Card moves up and out of envelope
        .to('.animation-card', {scale: 1, translateY: 0, duration: 1}, 4.1)
        .to(
          '.animation-card__wrapper',
          {
            keyframes: [
              {translateY: `+=${envelopeHeight / 2}`, duration: 0.6, ease: 'linear'},
              {translateY: '-10%', duration: 1, ease: 'power1.inOut'},
              {zIndex: 10, duration: 0},
              {translateY: '0', duration: 1, ease: 'power1.inOut'},
            ],
          },
          2.5
        )
        .set('.animation-replay-btn', {opacity: '1'}, 5.5);

      tlRef.current = {envelopeTimeline, cardTimeline};

      if (noAnimation || hasPlayedRef.current) {
        // Show final animation state
        tlRef.current.envelopeTimeline.progress(1);
        tlRef.current.cardTimeline.progress(1);
      } else if (shouldPlayRef.current) {
        // If we were supposed to play but got reverted, play again
        tlRef.current.envelopeTimeline.restart();
        tlRef.current.cardTimeline.restart();
        shouldPlayRef.current = false;
      }
    },
    {
      scope: containerRef,
      dependencies: [containerWidth, containerHeight],
      revertOnUpdate: true,
    }
  );

  const reset = useCallback(() => {
    shouldPlayRef.current = false;
    if (tlRef.current && !noAnimation) {
      setAnimationComplete(false);
      // Reset to beginning state and pause
      tlRef.current.envelopeTimeline.progress(0);
      tlRef.current.envelopeTimeline.pause();
      tlRef.current.cardTimeline.progress(0);
      tlRef.current.cardTimeline.pause();
      hasPlayedRef.current = false;
    }
  }, [noAnimation, hasPlayedRef, setAnimationComplete]);

  const play = useCallback(() => {
    shouldPlayRef.current = true;
    if (tlRef.current && !noAnimation) {
      tlRef.current.envelopeTimeline.restart();
      tlRef.current.cardTimeline.restart();
    }
  }, [noAnimation]);

  return {
    play,
    reset,
  };
};
